import { useEffect, useState } from "react";
import { Box, Typography, Container, Alert, Button, Paper } from "@mui/material";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useGlobalState } from "./context/global-context";
import { useHGlobalController } from "./context/global-controller";
import { getFormattedEntityName } from "../../utils/entity-formatter";

interface AppInitializerProps {
    children: React.ReactNode;
}

export function AppInitializer({ children }: AppInitializerProps) {
    const { applicationData, parametersPortal, error } = useGlobalState();
    const [initialLoading, setInitialLoading] = useState(true);
    const { getData } = useHGlobalController();

    useEffect(() => {
        const onInit = async () => {
            await getData();
            setInitialLoading(false);
        };

        if (applicationData === undefined && !error) {
            onInit();
        } else {
            setInitialLoading(false);
        }
    }, []);

    // Atualiza título e favicon quando os dados da entidade são carregados
    useEffect(() => {
        if (applicationData?.entity) {
            // Atualiza o título da página usando o nome formatado em UPPERCASE
            const entityName = getFormattedEntityName(applicationData.entity);
            document.title = `PORTAL DA ${entityName}`.toUpperCase();

            // Atualiza o favicon
            const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
            if (favicon && applicationData.entity.brandLogo) {
                favicon.href = `data:image/png;base64,${applicationData.entity.brandLogo}`;
            } else if (favicon && applicationData.entity.stateBrandLogo) {
                favicon.href = applicationData.entity.stateBrandLogo;
            }
        }
    }, [applicationData]);

    // Dispara evento de atualização de tema quando parametersPortal são carregados
    useEffect(() => {
        if (parametersPortal?.theme !== undefined) {
            const event = new CustomEvent('portalThemeUpdate', {
                detail: { themeNumber: parametersPortal.theme }
            });
            window.dispatchEvent(event);
        }
    }, [parametersPortal]);

    const handleRetry = () => {
        setInitialLoading(true);
        getData().finally(() => setInitialLoading(false));
    };

    // Mesma marcação da tela de carregamento do public/index.html (estilos definidos lá),
    // para a troca entre o HTML estático e o React ser imperceptível.
    const loadingComponent = (
        <div className="boot-splash boot-splash--instant" role="status" aria-live="polite">
            <div className="boot-splash__bar" />
            <div className="boot-splash__mark">
                <div className="boot-splash__ring" />
                <div className="boot-splash__icon">
                    <AccountBalanceIcon aria-hidden="true" />
                </div>
            </div>
            <div className="boot-splash__text">
                <p className="boot-splash__title">Portal da Entidade</p>
                <p className="boot-splash__subtitle">Preparando o portal…</p>
            </div>
        </div>
    );

    const errorComponent = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: 'background.default',
                px: 2,
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2,
                    }}
                >
                    <ErrorOutlineIcon
                        sx={{
                            fontSize: 80,
                            color: 'error.main',
                            mb: 2,
                        }}
                    />
                    <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
                        {error?.statusCode === 500 ? 'Erro Inesperado' : 'Erro ao Carregar'}
                    </Typography>
                    {error?.statusCode !== 500 && (
                        <Alert severity="error" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
                            <Typography variant="body2" component="div">
                                <strong>Detalhes do erro:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {error?.message}
                            </Typography>
                            {error?.statusCode && (
                                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                                    Código: {error.statusCode}
                                </Typography>
                            )}
                        </Alert>
                    )}
                    {error?.statusCode === 500 && (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
                            Ocorreu um erro inesperado ao carregar a aplicação. Por favor, tente novamente.
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<RefreshIcon />}
                            onClick={handleRetry}
                        >
                            Tentar Novamente
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            endIcon={<OpenInNewIcon />}
                            href="https://docgedsistemas.com.br/"
                        >
                            Site DocGed
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );

    if (initialLoading) {
        return loadingComponent;
    }

    if (error) {
        return errorComponent;
    }

    return <>{children}</>;
}
